import ContainerHeader from 'components/common/ContainerHeader';
import DefaultButton from 'components/common/DefaultButton';
import Pagination from 'components/common/Pagination';
import ScreenLayout from 'components/common/ScreenLayout';
import TableSortButton, {SortOrder} from 'components/common/TableSortButton';
import {getOperationsAnalyticsSites} from 'function/operationsAnalyticsApi';
import {
  OperationsAnalyticsSiteMetric,
  OperationsAnalyticsSiteSortBy,
  OperationsAnalyticsSites as SitesResponse,
} from 'interfaces/operationsAnalytics';
import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';

const METRIC_LABELS: Record<OperationsAnalyticsSiteMetric, string> = {
  operating: '운영 현장',
  active: '활성 현장',
  lowActivity: '저활성 현장',
  dormant: '휴면 현장',
  notStarted: '미착수 현장',
  new: '기간 내 신규 현장',
  endingD30: '종료 임박 D-30 현장',
};

const SORT_KEYS: OperationsAnalyticsSiteSortBy[] = [
  'tongPlaceName',
  'contractorName',
  'constructionState',
  'usageState',
  'constructionStartDate',
  'lastApprovedAttendanceDate',
];

const OperationsAnalyticsSites = () => {
  const userInfo = useRecoilValue(userInfoState);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const metricParam = searchParams.get('metric');
  const metric = Object.prototype.hasOwnProperty.call(
    METRIC_LABELS,
    metricParam ?? '',
  )
    ? (metricParam as OperationsAnalyticsSiteMetric)
    : 'operating';
  const startDate = searchParams.get('startDate') ?? '';
  const endDate = searchParams.get('endDate') ?? '';
  const currentPage = Math.max(Number(searchParams.get('page')) || 1, 1);
  const sortParam = searchParams.get('sortBy');
  const sortBy = SORT_KEYS.includes(sortParam as OperationsAnalyticsSiteSortBy)
    ? (sortParam as OperationsAnalyticsSiteSortBy)
    : 'tongPlaceName';
  const sortOrder: SortOrder =
    searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc';
  const [keyword, setKeyword] = useState(searchParams.get('keyword') ?? '');
  const [sites, setSites] = useState<SitesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  useEffect(() => {
    getOperationsAnalyticsSites({
      userInfo,
      metric,
      startDate,
      endDate,
      keyword: searchParams.get('keyword') ?? '',
      page: currentPage,
      count: 20,
      sortBy,
      sortOrder,
      setIsLoading,
      setErrorModal,
    }).then(result => result && setSites(result));
  }, [
    userInfo,
    metric,
    startDate,
    endDate,
    currentPage,
    searchParams,
    sortBy,
    sortOrder,
  ]);

  const updateQuery = (next: {
    page?: number;
    keyword?: string;
    sortBy?: OperationsAnalyticsSiteSortBy;
    sortOrder?: SortOrder;
  }) => {
    const query = new URLSearchParams(searchParams);
    query.set('page', String(next.page ?? 1));
    if (next.keyword !== undefined) {
      next.keyword
        ? query.set('keyword', next.keyword)
        : query.delete('keyword');
    }
    if (next.sortBy) {
      query.set('sortBy', next.sortBy);
      query.set('sortOrder', next.sortOrder ?? 'asc');
    }
    setSearchParams(query);
  };
  const setCurrentPage: React.Dispatch<React.SetStateAction<number>> = next => {
    const page = typeof next === 'function' ? next(currentPage) : next;
    updateQuery({page});
  };
  const changeSort = (nextSortBy: OperationsAnalyticsSiteSortBy) =>
    updateQuery({
      sortBy: nextSortBy,
      sortOrder: sortBy === nextSortBy && sortOrder === 'asc' ? 'desc' : 'asc',
    });

  const sortHeader = (label: string, key: OperationsAnalyticsSiteSortBy) => (
    <th
      aria-sort={
        sortBy === key
          ? sortOrder === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }>
      <TableSortButton
        label={label}
        active={sortBy === key}
        order={sortOrder}
        onClick={() => changeSort(key)}
      />
    </th>
  );

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader mainTitle="operationsAnalytics" />
        <Inner>
          <BackButton
            type="button"
            onClick={() =>
              navigate(
                `/operationsAnalytics?startDate=${startDate}&endDate=${endDate}`,
              )
            }>
            ← 운영 현황 분석
          </BackButton>
          <PageTitle>{METRIC_LABELS[metric]} 목록</PageTitle>
          <Meta>
            {startDate} ~ {endDate} · {sites?.criteria} · 공사 상태는 현재 값
            기준
          </Meta>
          <Filter>
            <SearchInput
              aria-label="현장명 또는 시공사명"
              value={keyword}
              placeholder="현장명 또는 시공사명"
              maxLength={100}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setKeyword(event.target.value)
              }
            />
            <DefaultButton
              text="toSearch"
              actived={1}
              buttonStyle={{width: minmax(90), height: minmax(40)}}
              onClick={() => updateQuery({keyword})}
            />
          </Filter>

          <Count>총 {sites?.totalCount ?? 0}개 현장</Count>
          <Table>
            <thead>
              <tr>
                {sortHeader('현장명', 'tongPlaceName')}
                {sortHeader('시공사', 'contractorName')}
                {sortHeader('공사 상태', 'constructionState')}
                {sortHeader('사용 상태', 'usageState')}
                {sortHeader('공사기간', 'constructionStartDate')}
                {sortHeader('마지막 승인 출근일', 'lastApprovedAttendanceDate')}
              </tr>
            </thead>
            <tbody>
              {sites?.items.map(site => (
                <tr key={site.tongPlaceUUID}>
                  <td>{site.tongPlaceName}</td>
                  <td>{site.contractorName ?? '-'}</td>
                  <td>{site.constructionState}</td>
                  <td>{site.usageState ?? '-'}</td>
                  <td>
                    {site.constructionStartDate ?? '-'} ~{' '}
                    {site.constructionEndDate ?? '-'}
                  </td>
                  <td>{site.lastApprovedAttendanceDate ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {(sites?.totalPage ?? 0) > 1 && (
            <Pagination
              totalPage={sites!.totalPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Inner>
      </Container>
    </ScreenLayout>
  );
};

export default OperationsAnalyticsSites;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;
const Inner = styled.main`
  padding: ${minmax(20)};
`;
const BackButton = styled.button`
  margin-bottom: ${minmax(12)};
  color: ${colors.primary2};
  font-size: ${minmax(16)};
`;
const PageTitle = styled.h1`
  width: fit-content;
  margin-bottom: ${minmax(10)};
  border-bottom: 3px solid ${colors.primary};
  font-size: ${minmax(25)};
`;
const Meta = styled.p`
  margin-bottom: ${minmax(18)};
  color: ${colors.graniteGray};
  font-size: ${minmax(16)};
`;
const Filter = styled.div`
  display: flex;
  gap: ${minmax(8)};
  margin-bottom: ${minmax(16)};
`;
const SearchInput = styled.input`
  width: ${minmax(320)};
  height: ${minmax(40)};
  padding: 0 ${minmax(10)};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(6)};
  font-size: ${minmax(16)};
`;
const Count = styled.p`
  margin-bottom: ${minmax(10)};
  font-size: ${minmax(17)};
  font-weight: 600;
`;
const Table = styled.table`
  width: 100%;
  margin-bottom: ${minmax(24)};
  border-collapse: collapse;
  border-bottom: 1px solid ${colors.darkGray};

  th,
  td {
    height: ${minmax(52)};
    padding: ${minmax(10)};
    border-top: 1px solid ${colors.lightGrey};
    font-size: ${minmax(16)};
    text-align: center;
  }
  th {
    background-color: #f7f7f7;
  }
`;
